import React, { useState, useEffect } from "react";
import ShowLicenseDialog from "./ShowLicenseDialog";
import { useLocation } from "react-router-dom";
import { updateOwnerDetails } from '../api/licenseService';
import {
    Container,
    Typography,
    Button,
    CircularProgress,
    Alert,
    Box,
    Paper,
} from "@mui/material";

const API_URL = "/api/license/token/validate/";

const createTrial = async (tillId) => {
    const response = await fetch(`/api/payment/trial?tillId=${tillId}`, {
        method: "GET"
    });
    const data = await response.text();
    alert(data);
    window.location.reload(true);
};

const createCheckout = async (tillId, licenseType) => {
    const response = await fetch(`/api/payment/checkout?tillId=${tillId}&licenseType=${licenseType}`, {
        method: "GET"
    });
    const data = await response.json();
    window.location.href = data.url; // Redirect to Stripe
};

const cancelSubscription = async (tillId, product) => {
    const response = await fetch(`/api/payment/cancel?tillId=${tillId}&product=${product}`, {
        method: "GET"
    });

    if (!response.ok) {
        console.log("Error Cancelling Subscription!");
        console.log(await response.text());
    }
};

const updateSubscription = async (tillId, product, licenseType) => {
    const response = await fetch(`/api/payment/update?tillId=${tillId}&product=${product}&licenseType=${licenseType}`, {
        method: "GET"
    });

    if (!response.ok) {
        console.log("Error Updating Subscription!");
        console.log(await response.text());
    }
};

function isLicenseExpired(license) {
    if (!license?.validUntil) return true;
    const expiry = new Date(license.validUntil);
    return expiry < new Date();
}

const openCustomerPortal = async (licenseId, token) => {
    const res = await fetch(`/api/payment/portal?licenseId=${licenseId}&token=${token}`);
    const data = await res.json();
    window.location.href = data.url;
};

export default function ValidateLicensePage() {
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const token = params.get("token");

    const [license, setLicense] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [tokenExpired, setTokenExpired] = useState(false);
    const [loading, setLoading] = useState(true);
    const [tillId, setTillId] = useState("");

    // --- central function to fetch license using token
    const fetchLicense = async () => {
        try {
            const res = await fetch(`${API_URL}${token}`);
            if (res.status === 404) {
                setNotFound(true);
                setLicense(null);
                const body = await res.text();
                setTillId(body);
                return null;
            }
            if (res.status === 401) {
                setTokenExpired(true);
                return null;
            }
            const data = await res.json();
            if (data) {
                setLicense(data);
                setTillId(data.tillId);
            }
            return data;
        } catch (err) {
            setNotFound(true);
            return null;
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!token) {
            setNotFound(true);
            setLoading(false);
            return;
        }
        setLoading(true);
        fetchLicense();
    }, [token]);

    if (loading) {
        return (
            <Container maxWidth="sm" sx={{ mt: 8, textAlign: "center" }}>
                <CircularProgress />
                <Typography variant="h6" sx={{ mt: 2 }}>
                    Lade Lizenzdaten...
                </Typography>
            </Container>
        );
    }

    if (tokenExpired) {
        return (
            <Container maxWidth="sm" sx={{ mt: 8 }}>
                <Alert severity="warning" sx={{ mb: 3 }}>
                    Ihr Lizenz-Token ist abgelaufen. Bitte rufen Sie diese Seite erneut über Ihre SuperKasse-Software auf.
                </Alert>
            </Container>
        );
    }

    if (notFound) {
        return (
            <Container maxWidth="sm" sx={{ mt: 8 }}>
                <Alert severity="error" sx={{ mb: 3 }}>
                    Es konnte keine gültige Lizenz gefunden werden.
                    Um eine Lizenz zu erwerben, klicken Sie bitte auf den Button "Lizenz erwerben"
                    oder kontaktieren Sie SuperKasse GmbH.
                </Alert>
                <Box display="flex" flexDirection="column" gap={2}>
                    <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        onClick={() => createCheckout(tillId, "Fixed")}
                    >
                        Jahresabo erwerben
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        onClick={() => createCheckout(tillId, "Subscription")}
                    >
                        Monatsabo erwerben
                    </Button>
                    <Button
                        variant="outlined"
                        color="secondary"
                        fullWidth
                        onClick={() => createTrial(tillId)}
                    >
                        Demo Lizenz erstellen
                    </Button>
                </Box>
            </Container>
        );
    }

    const saveOwnerDetails = async (details, restaurantId) => {
        await updateOwnerDetails(license.id, details, restaurantId);
    };

    return (
        <Container maxWidth="sm" sx={{ mt: 8 }}>
            <Paper elevation={3} sx={{ p: 4 }}>
                <Typography variant="h5" gutterBottom>
                    Lizenzdetails
                </Typography>
                <ShowLicenseDialog
                    license={license}
                    token={token}                   // <-- pass token down
                    fetchLicense={fetchLicense}     // <-- pass fetch function down
                    onSaveOwnerDetails={saveOwnerDetails}
                    onCreateCheckout={createCheckout}
                    onCancelSubscription={cancelSubscription}
                    onUpdateSubscription={updateSubscription}
                    onOpenCustomerPortal={openCustomerPortal}
                />
                {isLicenseExpired(license) && (
                    <Alert severity="warning" sx={{ mt: 3, mb: 2 }}>
                        Diese Lizenz ist abgelaufen.
                    </Alert>
                )}
            </Paper>
        </Container>
    );
}
